import { message, Modal, notification, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import {
  SwapOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  NodeIndexOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import Link from "antd/es/typography/Link";
import { useNavigate } from "react-router-dom";
import CompareStepper from "../generic/CompareStepper";
import CompareModalHeader from "../generic/CompareModalHeader";
import DataSourcePicker from "./DataSourcePicker";
import MapFields from "./MapFields";
import SelectTest from "./SelectTest";
import { getTestCaseMetaAsTree } from "../../service/suite.service";
import { transformData, transformDataCustom } from "../../helper/array_helper";
import { addMapping, getAllConnection } from "../../service/db.service";
import { getCurrentDateTimeInFormat } from "../../helper/date_helper";
import {
  createJob,
  updateJob,
  pollJobStatus,
  fileToFileCompare,
  dbToDbCompare,
  fileToDbCompareWithMultiPart,
} from "../../service/run.service";
import { useUser } from "../../context/UserContext";
import "../../styles/compareWizard.css";

const kindLabel = (k) => (k === "db" ? "Database" : k === "file" ? "File" : "—");
const kindIcon = (k) =>
  k === "db" ? <DatabaseOutlined /> : <FileTextOutlined />;
// Column-panel title follows the user's A/B choice (display), independent of the
// endpoint orientation (which is normalised in the payload — see clickRun).
const sourceLabel = (side, letter) =>
  `Source ${letter} · ${kindLabel(side?.kind)}`;

// One unified "New Reconciliation" wizard over the three EXISTING compare
// endpoints. The two sources are each independently a file or a database; the
// reconciliation TYPE is derived from the two picks, never chosen up front.
function NewReconciliationWizard(props) {
  const { isOpen, onClose, seed, initialMappingName } = props;
  const { email } = useUser();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const [step, setStep] = useState(1);
  const [sideA, setSideA] = useState(null); // { kind, payload }
  const [sideB, setSideB] = useState(null);
  const [swapCount, setSwapCount] = useState(0);

  const [dbConnections, setDbConnections] = useState([]);
  const [environments, setEnvironments] = useState([]);

  const [mappingDataSource, setMappingDataSource] = useState([]);
  const [mappingName, setMappingName] = useState("");
  const [mappingType, setMappingType] = useState("");
  const [fieldLevelTest, setFieldLevelTest] = useState([]);
  const [rowLevelTests, setRowLevelTest] = useState([]);

  // Fetch DB connections once per open and seed source kinds (quick-start cards).
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSideA(seed?.aKind ? { kind: seed.aKind, payload: null } : null);
    setSideB(seed?.bKind ? { kind: seed.bKind, payload: null } : null);
    setSwapCount(0);
    setMappingDataSource([]);
    setMappingName("");
    setMappingType("");
    setFieldLevelTest([]);
    getAllConnection().then((res) => {
      if (res.status === 200) {
        setDbConnections(res.data);
        setEnvironments([
          ...new Set(res.data.map((c) => c.environmentName)),
        ]);
      }
    });
  }, [isOpen, seed]);

  const handleJobIdClick = () => navigate("/jobs");

  const handleClose = () => {
    Modal.confirm({
      title: "You will lose all the changes if you cancel",
      content: "Are you sure you want to cancel?",
      okText: "Yes",
      cancelText: "No",
      onOk() {
        onClose();
        setStep(1);
      },
    });
  };

  // ── Source steps ────────────────────────────────────────────
  const onSideA = (v) => {
    setSideA(v);
    setStep(2);
  };
  const onSideB = (v) => {
    setSideB(v);
    setStep(3);
  };

  // Flip A ⇄ B (kind + payload) in one click; the built mapping depends on the
  // A/B headers, so clear it and remount MapFields (via swapCount key).
  const swapSides = () => {
    setSideA(sideB);
    setSideB(sideA);
    setMappingDataSource([]);
    setMappingName("");
    setSwapCount((c) => c + 1);
    if (step > 2) setStep(3);
  };

  const fileSide = () => (sideA?.kind === "file" ? sideA : sideB);
  const dbSide = () => (sideA?.kind === "db" ? sideA : sideB);

  // ── Map step ────────────────────────────────────────────────
  const getTestCaseTree = () => {
    getTestCaseMetaAsTree("SQL", "Row Level").then((res) => {
      if (res.status === 200) setRowLevelTest(transformData(res.data));
    });
  };

  const confirmMapFields = (_tabIndex, data, name, fieldLevelData, type) => {
    getTestCaseTree();
    setFieldLevelTest(transformDataCustom(fieldLevelData));
    setMappingDataSource(data);
    setMappingName(name);
    setMappingType(type);
    setStep(4);
  };

  // Normalise the db-file mapping to the endpoint's orientation (File = source1).
  // When the file side is A, the mapping is already File=source1 → no change
  // (byte-identical to DbFileCompareModal today). When A=Database, swap each row
  // and translate the field-test names (which are source1 column names) to their
  // paired File column via the 1:1 row mapping.
  const orientDbFile = (rows, fieldTests) => {
    if (sideA?.kind === "file") return { rows, fieldTests };
    const swapped = rows.map((r) => ({
      ...r,
      source1: r.source2,
      source2: r.source1,
    }));
    const pair = new Map(rows.map((r) => [r.source1, r.source2]));
    const tests = (fieldTests || []).map((t) => (pair.has(t) ? pair.get(t) : t));
    return { rows: swapped, fieldTests: tests };
  };

  // ── Run ─────────────────────────────────────────────────────
  const notifySync = (type, title, jobId, subCategory, errorMessage) => {
    if (type === "success") {
      api.success({
        message: title,
        description: (
          <span>
            {subCategory} <Link onClick={handleJobIdClick}>{jobId}</Link> is now
            complete. Click on the link to view Job.
          </span>
        ),
      });
    } else {
      api.error({
        message: title,
        description: (
          <span>
            Error was encountered with {subCategory} {jobId}. Error message :{" "}
            {errorMessage}
          </span>
        ),
      });
    }
  };

  const clickRun = (saveMapping, row, field) => {
    const kinds = [sideA.kind, sideB.kind].sort().join("-"); // db-file|file-file|db-db

    // For db-file, both the saved mapping and the run payload must be oriented
    // File=source1; for the symmetric cases nothing changes.
    let rows = mappingDataSource;
    let field2 = field;
    if (kinds === "db-file") {
      const o = orientDbFile(mappingDataSource, field);
      rows = o.rows;
      field2 = o.fieldTests;
    }

    if (saveMapping && mappingType === "new") {
      addMapping(rows)
        .then(() => message.success("Mapping added"))
        .catch((err) =>
          message.error(err.response?.data?.message || "Something went wrong!", 8),
        );
    } else if (saveMapping) {
      message.info("Existing mapping used to run!");
    }

    onClose();
    setStep(1);

    if (kinds === "file-file") runFileToFile(field, row);
    else if (kinds === "db-db") runDbToDb(field, row);
    else runFileToDb(field2, row, rows);
  };

  const runFileToFile = (field, row) => {
    const taskType = "On Demand";
    const taskCategory = "Compare";
    const taskSubCategory = "File to File";
    createJob(
      taskType,
      taskCategory,
      taskSubCategory,
      "In Progress",
      email,
      "File to File",
      "Not Applicable",
    ).then((res) => {
      if (res.status !== 200) return;
      const jobId = res.data.jobId;
      const subCategory = res.data.taskCategory;
      fileToFileCompare(
        jobId,
        taskCategory,
        taskSubCategory,
        taskType,
        "Job",
        sideA.payload.file, // testFileOne  (source 1 = A)
        sideB.payload.file, // testFileTwo  (source 2 = B)
        sideA.payload.delimiter,
        sideB.payload.delimiter,
        mappingName,
        row,
        field,
        mappingDataSource,
      )
        .then((r) => {
          if (r.status === 200) {
            updateJob(jobId, "Completed", "", getCurrentDateTimeInFormat());
            notifySync("success", "Run complete", jobId, subCategory);
          }
        })
        .catch((err) => {
          updateJob(jobId, "Error", err.code, err.message);
          notifySync("error", err.code, jobId, subCategory, err.message);
        });
    });
  };

  const runDbToDb = (field, row) => {
    const taskType = "On Demand";
    const taskCategory = "Compare";
    const taskSubCategory = "Database to Database";
    createJob(
      taskType,
      taskCategory,
      taskSubCategory,
      "In Progress",
      email,
      sideA.payload.suiteName,
      "Not Applicable",
    ).then((res) => {
      if (res.status !== 200) return;
      const jobId = res.data.jobId;
      const subCategory = res.data.taskCategory;
      dbToDbCompare(
        jobId,
        sideA.payload.suiteName, // suiteIdOne  (source 1 = A)
        sideB.payload.suiteName, // suiteIdTwo  (source 2 = B)
        taskCategory,
        taskSubCategory,
        taskType,
        "Job",
        sideA.payload.sql,
        sideB.payload.sql,
        sideA.payload.dbName,
        sideB.payload.dbName,
        mappingName,
        row,
        field,
        mappingDataSource,
      )
        .then((r) => {
          if (r.status === 200) {
            updateJob(jobId, "Completed", "", getCurrentDateTimeInFormat());
            notifySync("success", "Run complete", jobId, subCategory);
          }
        })
        .catch((err) => {
          updateJob(jobId, "Error", err.code, err.message);
          notifySync("error", err.code, jobId, subCategory, err.message);
        });
    });
  };

  // Async job flow (mirrors DbFileCompareModal): create job → poll → fire 202.
  const runFileToDb = (field, row, rows) => {
    const taskType = "On Demand";
    const taskCategory = "Compare";
    const taskSubCategory = "File to Database";
    const f = fileSide();
    const d = dbSide();
    const testSuite =
      (d.payload.suiteName || "").length < 1
        ? "Manually entered SQL"
        : d.payload.suiteName;

    createJob(taskType, taskCategory, taskSubCategory, "In Progress", email).then(
      (res) => {
        if (res.status !== 200) return;
        const jobId = res.data.jobId;
        const subCategory = res.data.taskCategory;
        message.info(
          `Job ${jobId} is running. You will be notified when complete.`,
        );
        pollJobStatus(jobId)
          .then(() => notifySync("success", "Run complete", jobId, subCategory))
          .catch((jobData) =>
            notifySync(
              "error",
              "Run failed",
              jobId,
              subCategory,
              jobData?.errorDescription || "Unknown error",
            ),
          );
        fileToDbCompareWithMultiPart(
          jobId,
          testSuite,
          f.payload.file, // inputFile  (file side)
          taskCategory,
          taskSubCategory,
          taskType,
          "Job",
          d.payload.sql, // sql        (db side)
          d.payload.dbName,
          mappingName,
          row,
          field, // already oriented File=source1
          rows, // mappingTableData, oriented File=source1
          f.payload.delimiter,
        ).catch((err) => {
          if (err.code !== "ERR_NETWORK_IO_SUSPENDED") {
            notifySync("error", "Run failed", jobId, subCategory, err.message);
          }
        });
      },
    );
  };

  // ── Steps ───────────────────────────────────────────────────
  const bothSidesChosen = sideA?.kind && sideB?.kind;

  const steps = [
    {
      key: 1,
      label: `Source A${sideA?.kind ? ` — ${kindLabel(sideA.kind)}` : ""}`,
      icon: sideA?.kind ? kindIcon(sideA.kind) : <SwapOutlined />,
      children: (
        <DataSourcePicker
          side="A"
          value={sideA}
          onChange={onSideA}
          dbConnections={dbConnections}
          environments={environments}
        />
      ),
    },
    {
      key: 2,
      label: `Source B${sideB?.kind ? ` — ${kindLabel(sideB.kind)}` : ""}`,
      icon: sideB?.kind ? kindIcon(sideB.kind) : <SwapOutlined />,
      children: (
        <DataSourcePicker
          side="B"
          value={sideB}
          onChange={onSideB}
          onBack={() => setStep(1)}
          dbConnections={dbConnections}
          environments={environments}
        />
      ),
    },
    {
      key: 3,
      label: "Map fields",
      icon: <NodeIndexOutlined />,
      children: (
        <MapFields
          key={swapCount}
          clickPrevious={() => setStep(2)}
          confirmMapFields={confirmMapFields}
          fileHeader={sideA?.payload?.header || []}
          sqlHeader={sideB?.payload?.header || []}
          uploadedFile={fileSide()?.payload?.file}
          dataSourceOneColumn={sourceLabel(sideA, "A")}
          dataSourceTwoColumn={sourceLabel(sideB, "B")}
          initialMappingName={initialMappingName}
        />
      ),
    },
    {
      key: 4,
      label: "Select tests",
      icon: <CheckSquareOutlined />,
      children: (
        <SelectTest
          clickPrevious={() => setStep(3)}
          rowLevelTests={rowLevelTests}
          fieldLevelTests={fieldLevelTest}
          mappingDataSource={mappingDataSource}
          clickRun={clickRun}
        />
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Modal
        open={isOpen}
        onCancel={handleClose}
        centered
        destroyOnClose
        maskClosable={false}
        width={780}
        footer={null}
      >
        <CompareModalHeader
          icon={<SwapOutlined />}
          title="New Reconciliation"
          subtitle="Pick two sources — the reconciliation type is set automatically"
        />

        {/* Sources summary + swap control */}
        {(sideA?.kind || sideB?.kind) && (
          <div className="recon-sources">
            <span className="recon-chip">
              A · {kindLabel(sideA?.kind)}
            </span>
            <button
              type="button"
              className="recon-swap"
              onClick={swapSides}
              title="Swap Source A and Source B"
              disabled={!bothSidesChosen}
            >
              <SwapOutlined />
            </button>
            <span className="recon-chip">
              B · {kindLabel(sideB?.kind)}
            </span>
          </div>
        )}

        <CompareStepper steps={steps} current={step} />
        <Tabs
          type="card"
          size="middle"
          items={steps}
          activeKey={step}
          renderTabBar={() => null}
        />
      </Modal>
    </div>
  );
}

export default NewReconciliationWizard;
